import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Trash2, ArrowLeft, Barcode, FileSpreadsheet, ArrowRightCircle, Package, Minus, ChevronDown } from 'lucide-react';
import api from '../lib/api';
import { titleCaseColor } from '../lib/utils';

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const SIZE_ORDER = ['S','M','L','XL','2XL','3XL','4XL','5XL','6XL','7XL','8XL','9XL','Free Size','32','34','36','38','40','42'];

const ColorComboInput = ({ value, availableColors, usedColors, onChange, testId }) => {
  const [open, setOpen] = useState(false);
  const [inputVal, setInputVal] = useState(value);
  const ref = useRef(null);

  useEffect(() => { setInputVal(value); }, [value]);

  useEffect(() => {
    const handleClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const options = (availableColors || []).filter(c => c && c !== value && !usedColors.includes(c));

  const handleSelect = (color) => {
    const tc = titleCaseColor(color);
    setInputVal(tc);
    setOpen(false);
    if (tc && tc !== value) onChange(tc);
  };

  const handleBlur = () => {
    setTimeout(() => {
      const tc = titleCaseColor(inputVal);
      if (tc && tc !== value) onChange(tc);
    }, 150);
  };

  return (
    <div ref={ref} className="relative">
      <div className="flex items-center">
        <input
          type="text"
          value={inputVal}
          onChange={(e) => setInputVal(e.target.value)}
          onBlur={handleBlur}
          onFocus={() => setOpen(true)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.target.blur(); setOpen(false); } }}
          className="h-9 w-full px-2 pr-7 text-sm font-semibold rounded border-2 border-blue-200 bg-blue-50/50 focus:border-blue-500 focus:bg-white focus:outline-none transition-colors"
          data-testid={testId}
        />
        <button
          type="button"
          className="absolute right-1 top-1/2 -translate-y-1/2 p-0.5 text-gray-400 hover:text-blue-600"
          onClick={() => setOpen(!open)}
          tabIndex={-1}
        >
          <ChevronDown className="w-4 h-4" />
        </button>
      </div>
      {open && options.length > 0 && (
        <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border-2 border-blue-200 rounded-lg shadow-lg max-h-48 overflow-y-auto" data-testid={`${testId}-dropdown`}>
          {options.map(c => (
            <button
              key={c}
              type="button"
              className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50 font-medium transition-colors"
              onMouseDown={(e) => { e.preventDefault(); handleSelect(c); }}
              data-testid={`${testId}-option-${c}`}
            >
              {c}
            </button>
          ))}
          <div className="px-3 py-1.5 text-[10px] text-gray-400 border-t">
            Or type manually above
          </div>
        </div>
      )}
    </div>
  );
};

const ProcessSOItems = ({ user }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const soData = location.state?.soData || null;
  const selectedItems = location.state?.selectedItems || [];

  const [items, setItems] = useState([]);
  const [brands, setBrands] = useState([]);
  const [productColors, setProductColors] = useState({}); // { productId: [color1, color2, ...] }
  const [productSizes, setProductSizes] = useState({}); // { productId: [size1, size2, ...] }
  const [allSizes, setAllSizes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [addColorInput, setAddColorInput] = useState({}); // { itemIdx: true/false }
  const [addSizeInput, setAddSizeInput] = useState({}); // { "itemIdx::color": true/false }
  const [searchDesign, setSearchDesign] = useState('');

  useEffect(() => {
    if (!soData || selectedItems.length === 0) {
      toast.error('No items selected');
      navigate(-1);
      return;
    }
    // Build editable items from selected SO items
    const editableItems = selectedItems.map(item => {
      const colorMap = {};
      (item.variants || []).forEach(v => {
        const pending = Math.max(0, (v.qty_ordered || 0) - (v.qty_dispatched || 0));
        if (pending <= 0) return;
        const color = v.color || 'Other';
        if (!colorMap[color]) colorMap[color] = { enabled: true, sizes: {} };
        colorMap[color].sizes[v.size] = {
          qty: pending,
          originalQty: pending,
          rate: v.wsp || 0,
          enabled: true,
        };
      });
      return {
        id: item.id,
        design_code: item.design_code,
        product_id: item.product_id,
        brand_id: item.brand_id,
        images: item.images || [],
        enabled: true,
        colors: colorMap,
      };
    }).filter(item => Object.keys(item.colors).length > 0);

    setItems(editableItems);
    // Fetch brands, enums, and product colours for dropdowns
    api.get('/brands').then(r => setBrands(r.data)).catch(() => {});
    api.get('/enums').then(r => {
      const sizes = r.data?.sizes || [];
      setAllSizes(sizes.sort((a, b) => {
        const ia = SIZE_ORDER.indexOf(a), ib = SIZE_ORDER.indexOf(b);
        return (ia === -1 ? 999 : ia) - (ib === -1 ? 999 : ib);
      }));
    }).catch(() => {});
    // Fetch available colours and sizes per product from catalogue
    const productIds = [...new Set(editableItems.map(i => i.product_id).filter(Boolean))];
    if (productIds.length > 0) {
      Promise.allSettled(productIds.map(pid => api.get(`/products/${pid}`))).then(results => {
        const colorMap = {};
        const sizeMap = {};
        results.forEach((r, idx) => {
          if (r.status === 'fulfilled' && r.value.data) {
            const prod = r.value.data;
            const colors = new Set();
            const sizes = new Set();
            const ic = prod.image_colors;
            if (Array.isArray(ic)) ic.forEach(c => colors.add(c));
            else if (ic && typeof ic === 'object') Object.values(ic).forEach(c => { if (c) colors.add(c); });
            (prod.skus || []).forEach(s => { if (s.color) colors.add(s.color); if (s.size) sizes.add(s.size); });
            colorMap[productIds[idx]] = [...colors].sort();
            sizeMap[productIds[idx]] = [...sizes].sort((a, b) => {
              const ia = SIZE_ORDER.indexOf(a), ib = SIZE_ORDER.indexOf(b);
              return (ia === -1 ? 999 : ia) - (ib === -1 ? 999 : ib);
            });
          }
        });
        setProductColors(colorMap);
        setProductSizes(sizeMap);
      });
    }
  }, []);

  const getBrandName = (bid) => brands.find(b => b.id === bid)?.name || '';

  // Collect all active sizes across all items
  const activeSizes = (() => {
    const sizeSet = new Set();
    items.forEach(item => {
      if (!item.enabled) return;
      Object.entries(item.colors).forEach(([, cd]) => {
        if (!cd.enabled) return;
        Object.keys(cd.sizes).forEach(s => {
          if (cd.sizes[s].enabled) sizeSet.add(s);
        });
      });
    });
    return SIZE_ORDER.filter(s => sizeSet.has(s));
  })();

  const updateQty = (itemIdx, color, size, newQty) => {
    setItems(prev => {
      const next = [...prev];
      const item = { ...next[itemIdx], colors: { ...next[itemIdx].colors } };
      item.colors[color] = { ...item.colors[color], sizes: { ...item.colors[color].sizes } };
      item.colors[color].sizes[size] = { ...item.colors[color].sizes[size], qty: Math.max(0, parseInt(newQty) || 0) };
      next[itemIdx] = item;
      return next;
    });
  };

  const updateRate = (itemIdx, color, size, newRate) => {
    setItems(prev => {
      const next = [...prev];
      const item = { ...next[itemIdx], colors: { ...next[itemIdx].colors } };
      item.colors[color] = { ...item.colors[color], sizes: { ...item.colors[color].sizes } };
      item.colors[color].sizes[size] = { ...item.colors[color].sizes[size], rate: Math.max(0, parseFloat(newRate) || 0) };
      next[itemIdx] = item;
      return next;
    });
  };

  const renameColor = (itemIdx, oldColor, newColor) => {
    const tc = titleCaseColor(newColor);
    if (!tc || tc === oldColor) return;
    setItems(prev => {
      const next = [...prev];
      const item = { ...next[itemIdx], colors: { ...next[itemIdx].colors } };
      // Move the color entry to the new key
      item.colors[newColor] = item.colors[oldColor];
      delete item.colors[oldColor];
      next[itemIdx] = item;
      return next;
    });
  };

  const toggleColor = (itemIdx, color) => {
    setItems(prev => {
      const next = [...prev];
      const item = { ...next[itemIdx], colors: { ...next[itemIdx].colors } };
      item.colors[color] = { ...item.colors[color], enabled: !item.colors[color].enabled };
      next[itemIdx] = item;
      return next;
    });
  };

  const toggleSize = (itemIdx, color, size) => {
    setItems(prev => {
      const next = [...prev];
      const item = { ...next[itemIdx], colors: { ...next[itemIdx].colors } };
      item.colors[color] = { ...item.colors[color], sizes: { ...item.colors[color].sizes } };
      item.colors[color].sizes[size] = { ...item.colors[color].sizes[size], enabled: !item.colors[color].sizes[size].enabled };
      next[itemIdx] = item;
      return next;
    });
  };

  const removeItem = (itemIdx) => {
    setItems(prev => prev.filter((_, i) => i !== itemIdx));
  };

  const addColorToItem = (itemIdx, colorName) => {
    const tc = titleCaseColor(colorName);
    if (!tc) return;
    setItems(prev => {
      const next = [...prev];
      const item = { ...next[itemIdx], colors: { ...next[itemIdx].colors } };
      if (item.colors[tc]) return prev;
      item.colors[tc] = { enabled: true, sizes: {} };
      next[itemIdx] = item;
      return next;
    });
    setAddColorInput(prev => ({ ...prev, [itemIdx]: false }));
  };

  const addSizeToColor = (itemIdx, color, size) => {
    if (!size.trim()) return;
    setItems(prev => {
      const next = [...prev];
      const item = { ...next[itemIdx], colors: { ...next[itemIdx].colors } };
      item.colors[color] = { ...item.colors[color], sizes: { ...item.colors[color].sizes } };
      if (item.colors[color].sizes[size]) return prev; // already exists
      item.colors[color].sizes[size] = { qty: 0, originalQty: 0, rate: 0, enabled: true };
      next[itemIdx] = item;
      return next;
    });
    setAddSizeInput(prev => ({ ...prev, [`${itemIdx}::${color}`]: false }));
  };

  // Build payload from editable items
  const buildPayload = () => {
    const payload = [];
    items.forEach(item => {
      if (!item.enabled) return;
      const variants = [];
      Object.entries(item.colors).forEach(([color, cd]) => {
        if (!cd.enabled) return;
        Object.entries(cd.sizes).forEach(([size, sd]) => {
          if (!sd.enabled || sd.qty <= 0) return;
          variants.push({ color, size, qty: sd.qty, rate: sd.rate });
        });
      });
      if (variants.length > 0) {
        payload.push({
          id: item.id,
          design_code: item.design_code,
          product_id: item.product_id,
          brand_id: item.brand_id,
          variants,
        });
      }
    });
    return payload;
  };

  const totalPieces = (() => {
    let total = 0;
    items.forEach(item => {
      if (!item.enabled) return;
      Object.entries(item.colors).forEach(([, cd]) => {
        if (!cd.enabled) return;
        Object.values(cd.sizes).forEach(sd => {
          if (sd.enabled) total += sd.qty;
        });
      });
    });
    return total;
  })();

  const totalDesigns = items.filter(i => i.enabled).length;

  const handleIssueBarcodes = async () => {
    const payload = buildPayload();
    if (payload.length === 0) { toast.error('No items with valid quantities'); return; }
    setLoading(true);
    try {
      const res = await api.post('/sales-orders/barcode-excel-custom', {
        so_id: soData.id,
        so_number: soData.so_number,
        items: payload,
      }, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
      const a = document.createElement('a'); a.href = url; a.download = `barcodes-${soData.so_number}.xlsx`; a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Barcode Excel downloaded!');
    } catch { toast.error('Failed to generate barcode Excel'); }
    finally { setLoading(false); }
  };

  const handleCreateSE = () => {
    const payload = buildPayload();
    if (payload.length === 0) { toast.error('No items with valid quantities'); return; }
    // Build a filtered SO object with edited quantities
    const filteredSO = {
      ...soData,
      items: payload.map(pi => ({
        id: pi.id,
        design_code: pi.design_code,
        product_id: pi.product_id,
        brand_id: pi.brand_id,
        variants: pi.variants.map(v => ({
          color: v.color,
          size: v.size,
          qty_ordered: v.qty,
          qty_dispatched: 0,
          wsp: v.rate,
        })),
      })),
    };
    navigate('/create-sales-entry', { state: { fromSO: filteredSO } });
  };

  if (!soData) return null;

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6 animate-fadeIn" data-testid="process-so-items-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate(`/sales-order/${soData.id}`)} data-testid="process-back-btn" className="text-gray-500 hover:text-gray-900">
            <ArrowLeft className="w-5 h-5 mr-1" />Back to SO
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900" data-testid="process-so-number">
              Process Items — {soData.so_number}
            </h1>
            <p className="text-sm text-gray-500">Review and edit selected items before issuing barcodes or creating SE</p>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4" data-testid="process-summary">
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-xs text-gray-500 mb-1">Designs</p>
            <p className="text-2xl font-bold text-gray-900">{totalDesigns}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-xs text-gray-500 mb-1">Total Pieces</p>
            <p className="text-2xl font-bold text-blue-700">{totalPieces}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-xs text-gray-500 mb-1">Sizes Active</p>
            <p className="text-2xl font-bold text-emerald-700">{activeSizes.length}</p>
          </CardContent>
        </Card>
      </div>

      {/* Search + Bulk Size Actions */}
      <div className="flex items-center gap-3 flex-wrap">
        <input
          type="text"
          value={searchDesign}
          onChange={(e) => setSearchDesign(e.target.value)}
          placeholder="Search design code..."
          className="h-9 w-64 px-3 text-sm border-2 border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
          data-testid="process-search-design"
        />
        {/* Bulk size add/remove */}
        <select
          className="h-9 px-3 text-sm border-2 border-red-200 rounded-lg bg-red-50/50 focus:outline-none"
          defaultValue=""
          onChange={(e) => {
            if (!e.target.value) return;
            const size = e.target.value;
            e.target.value = '';
            if (!window.confirm(`Remove size "${size}" from ALL designs?`)) return;
            setItems(prev => prev.map(item => {
              const colors = { ...item.colors };
              Object.keys(colors).forEach(c => {
                const cd = { ...colors[c], sizes: { ...colors[c].sizes } };
                delete cd.sizes[size];
                colors[c] = cd;
              });
              return { ...item, colors };
            }));
            toast.success(`Removed size ${size} from all designs`);
          }}
          data-testid="process-bulk-remove-size"
        >
          <option value="">Remove size from all...</option>
          {activeSizes.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select
          className="h-9 px-3 text-sm border-2 border-green-200 rounded-lg bg-green-50/50 focus:outline-none"
          defaultValue=""
          onChange={(e) => {
            if (!e.target.value) return;
            const size = e.target.value;
            e.target.value = '';
            setItems(prev => prev.map(item => {
              const colors = { ...item.colors };
              Object.keys(colors).forEach(c => {
                const cd = { ...colors[c], sizes: { ...colors[c].sizes } };
                if (!cd.sizes[size]) cd.sizes[size] = { qty: 0, originalQty: 0, rate: 0, enabled: true };
                colors[c] = cd;
              });
              return { ...item, colors };
            }));
            toast.success(`Added size ${size} to all designs`);
          }}
          data-testid="process-bulk-add-size"
        >
          <option value="">Add size to all...</option>
          {allSizes.filter(s => !activeSizes.includes(s)).map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      {/* Items */}
      {items.length === 0 ? (
        <Card><CardContent className="p-12 text-center text-gray-400">No items to process. Go back and select items.</CardContent></Card>
      ) : (
        <div className="space-y-4">
          {items.map((item, itemIdx) => {
            if (!item.enabled) return null;
            if (searchDesign && !item.design_code.toLowerCase().includes(searchDesign.toLowerCase())) return null;
            const firstImg = item.images[0];
            const colorEntries = Object.entries(item.colors).filter(([, cd]) => cd.enabled);
            const itemSizes = new Set();
            colorEntries.forEach(([, cd]) => {
              Object.entries(cd.sizes).forEach(([s, sd]) => { if (sd.enabled) itemSizes.add(s); });
            });
            const itemActiveSizes = SIZE_ORDER.filter(s => itemSizes.has(s));
            const itemTotalQty = colorEntries.reduce((sum, [, cd]) =>
              sum + Object.values(cd.sizes).reduce((s2, sd) => s2 + (sd.enabled ? sd.qty : 0), 0), 0);

            return (
              <Card key={item.id} className="overflow-hidden" data-testid={`process-item-${item.id}`}>
                <div className="bg-gradient-to-r from-slate-50 to-white border-b px-4 py-3 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {firstImg ? (
                      <img src={`${API_BASE}${firstImg}`} alt={item.design_code} className="w-12 h-12 rounded-lg object-cover border" />
                    ) : (
                      <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center text-gray-400">
                        <Package className="w-5 h-5" />
                      </div>
                    )}
                    <div>
                      <h3 className="font-bold text-gray-900 text-lg" data-testid={`process-item-design-${item.id}`}>{item.design_code}</h3>
                      {getBrandName(item.brand_id) && (
                        <p className="text-xs text-gray-500">{getBrandName(item.brand_id)}</p>
                      )}
                    </div>
                    <Badge className="bg-blue-50 text-blue-700 ml-2">{itemTotalQty} pcs</Badge>
                  </div>
                  <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700 hover:bg-red-50"
                    onClick={() => removeItem(itemIdx)} data-testid={`process-remove-item-${item.id}`}>
                    <Trash2 className="w-4 h-4 mr-1" /> Remove
                  </Button>
                </div>
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm border-collapse">
                      <thead>
                        <tr className="bg-[#1E1E23] text-white text-xs uppercase tracking-wider">
                          <th className="px-3 py-2 text-left font-medium border border-gray-700 w-36">Colour</th>
                          <th className="px-2 py-2 text-center font-medium border border-gray-700 w-14"></th>
                          {itemActiveSizes.map(s => (
                            <th key={s} className="px-2 py-2 text-center font-medium border border-gray-700 min-w-[70px]">{s}</th>
                          ))}
                          <th className="px-3 py-2 text-center font-medium border border-gray-700 w-20">Total</th>
                          <th className="px-2 py-2 text-center font-medium border border-gray-700 w-12"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {colorEntries.map(([color, cd]) => {
                          const colorQty = Object.values(cd.sizes).reduce((s, sd) => s + (sd.enabled ? sd.qty : 0), 0);
                          return (
                            <React.Fragment key={color}>
                              {/* Qty row */}
                              <tr className="bg-white hover:bg-gray-50/60 transition-colors">
                                <td className="px-2 py-1.5 font-medium text-gray-800 border border-gray-200" rowSpan="2">
                                  <ColorComboInput
                                    value={color}
                                    availableColors={productColors[item.product_id] || []}
                                    usedColors={Object.keys(item.colors)}
                                    onChange={(newColor) => renameColor(itemIdx, color, newColor)}
                                    testId={`process-color-input-${item.id}-${color}`}
                                  />
                                </td>
                                <td className="px-1 py-1 text-center text-[10px] text-gray-400 font-medium border border-gray-200 bg-gray-50">QTY</td>
                                {itemActiveSizes.map(size => {
                                  const sd = cd.sizes[size];
                                  if (!sd) return <td key={size} className="px-2 py-1 text-center border border-gray-200 bg-gray-50"><span className="text-gray-300">-</span></td>;
                                  return (
                                    <td key={size} className="px-1 py-1 text-center border border-gray-200">
                                      {sd.enabled ? (
                                        <input
                                          type="number"
                                          min="0"
                                          value={sd.qty || ''}
                                          onChange={(e) => updateQty(itemIdx, color, size, e.target.value)}
                                          onFocus={(e) => e.target.select()}
                                          placeholder="0"
                                          className="h-9 w-full text-center text-sm font-bold rounded border-2 border-gray-300 bg-white focus:border-emerald-500 focus:outline-none transition-colors [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                          data-testid={`process-qty-${item.id}-${color}-${size}`}
                                        />
                                      ) : (
                                        <button
                                          className="w-full h-9 text-gray-400 hover:text-gray-600 text-xs border-2 border-dashed border-gray-200 rounded"
                                          onClick={() => toggleSize(itemIdx, color, size)}
                                        >+</button>
                                      )}
                                    </td>
                                  );
                                })}
                                <td className="px-3 py-1.5 text-center font-bold text-gray-900 border border-gray-200 text-base" rowSpan="2">{colorQty}</td>
                                <td className="px-2 py-1.5 text-center border border-gray-200" rowSpan="2">
                                  <button className="text-gray-400 hover:text-red-500 transition-colors"
                                    onClick={() => toggleColor(itemIdx, color)}
                                    data-testid={`process-remove-color-${item.id}-${color}`}>
                                    <Minus className="w-4 h-4" />
                                  </button>
                                </td>
                              </tr>
                              {/* Rate row */}
                              <tr className="bg-amber-50/30">
                                <td className="px-1 py-1 text-center text-[10px] text-amber-600 font-medium border border-gray-200 bg-amber-50/50">RATE</td>
                                {itemActiveSizes.map(size => {
                                  const sd = cd.sizes[size];
                                  if (!sd || !sd.enabled) return <td key={size} className="px-2 py-0.5 text-center border border-gray-200 bg-gray-50"></td>;
                                  return (
                                    <td key={size} className="px-1 py-1 text-center border border-gray-200">
                                      <div className="flex items-center">
                                        <span className="text-xs text-gray-400 pl-1">₹</span>
                                        <input
                                          type="number"
                                          min="0"
                                          step="0.01"
                                          value={sd.rate || ''}
                                          onChange={(e) => updateRate(itemIdx, color, size, e.target.value)}
                                          onFocus={(e) => e.target.select()}
                                          placeholder="0"
                                          className="h-8 w-full text-center text-xs text-blue-700 font-bold rounded border-2 border-amber-200 bg-amber-50/50 focus:border-amber-500 focus:bg-white focus:outline-none transition-colors [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                          data-testid={`process-rate-${item.id}-${color}-${size}`}
                                        />
                                      </div>
                                    </td>
                                  );
                                })}
                              </tr>
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  {/* Add Size buttons per colour */}
                  <div className="px-4 py-2 border-t bg-blue-50/30 flex flex-wrap gap-2">
                    {colorEntries.map(([color]) => {
                      const key = `${itemIdx}::${color}`;
                      const usedSizesForColor = new Set(Object.keys(item.colors[color]?.sizes || {}));
                      const availSizes = (productSizes[item.product_id] || allSizes).filter(s => !usedSizesForColor.has(s));
                      if (addSizeInput[key]) {
                        return (
                          <div key={color} className="flex items-center gap-1">
                            <span className="text-xs text-gray-500">{color}:</span>
                            <select
                              className="h-7 px-2 text-xs border border-blue-300 rounded bg-white focus:outline-none"
                              defaultValue=""
                              onChange={(e) => { if (e.target.value) addSizeToColor(itemIdx, color, e.target.value); }}
                              data-testid={`process-add-size-select-${item.id}-${color}`}
                            >
                              <option value="">Select size...</option>
                              {availSizes.map(s => <option key={s} value={s}>{s}</option>)}
                            </select>
                            <button className="text-xs text-gray-400 hover:text-red-500" onClick={() => setAddSizeInput(prev => ({ ...prev, [key]: false }))}>✕</button>
                          </div>
                        );
                      }
                      return availSizes.length > 0 ? (
                        <button key={color} className="text-xs px-2 py-1 rounded border border-dashed border-blue-300 text-blue-600 hover:bg-blue-50 transition-colors"
                          onClick={() => setAddSizeInput(prev => ({ ...prev, [key]: true }))}
                          data-testid={`process-add-size-btn-${item.id}-${color}`}>
                          + Size for {color}
                        </button>
                      ) : null;
                    })}
                  </div>
                  {/* Add Colour */}
                  <div className="px-4 py-2 border-t bg-emerald-50/30 flex items-center gap-2 flex-wrap">
                    {addColorInput[itemIdx] ? (
                      <div className="flex items-center gap-1">
                        {(() => {
                          const usedColors = Object.keys(item.colors);
                          const availColors = (productColors[item.product_id] || []).filter(c => !usedColors.includes(c));
                          return availColors.length > 0 ? (
                            <select
                              className="h-7 px-2 text-xs border border-emerald-300 rounded bg-white focus:outline-none"
                              defaultValue=""
                              onChange={(e) => { if (e.target.value) addColorToItem(itemIdx, e.target.value); }}
                              data-testid={`process-add-color-select-${item.id}`}
                            >
                              <option value="">Select colour...</option>
                              {availColors.map(c => <option key={c} value={c}>{c}</option>)}
                            </select>
                          ) : null;
                        })()}
                        <input
                          type="text"
                          placeholder="Or type colour name + Enter"
                          className="h-7 px-2 text-xs border border-emerald-300 rounded bg-white focus:outline-none w-44"
                          onKeyDown={(e) => { if (e.key === 'Enter') { addColorToItem(itemIdx, e.target.value); e.target.value = ''; } }}
                          onBlur={(e) => { if (e.target.value.trim()) addColorToItem(itemIdx, e.target.value.trim()); }}
                          data-testid={`process-add-color-input-${item.id}`}
                        />
                        <button className="text-xs text-gray-400 hover:text-red-500" onClick={() => setAddColorInput(prev => ({ ...prev, [itemIdx]: false }))}>✕</button>
                      </div>
                    ) : (
                      <button className="text-xs px-3 py-1 rounded border border-dashed border-emerald-400 text-emerald-700 hover:bg-emerald-50 font-medium transition-colors"
                        onClick={() => setAddColorInput(prev => ({ ...prev, [itemIdx]: true }))}
                        data-testid={`process-add-color-btn-${item.id}`}>
                        + Add Colour
                      </button>
                    )}
                  </div>
                  {/* Show removed colors that can be re-enabled */}
                  {(() => {
                    const removedColors = Object.entries(item.colors).filter(([, cd]) => !cd.enabled);
                    if (removedColors.length === 0) return null;
                    return (
                      <div className="px-4 py-2 border-t bg-gray-50 flex items-center gap-2 flex-wrap">
                        <span className="text-xs text-gray-500">Removed:</span>
                        {removedColors.map(([color]) => (
                          <button key={color} className="text-xs px-2 py-0.5 rounded bg-gray-200 text-gray-600 hover:bg-emerald-100 hover:text-emerald-700 transition-colors"
                            onClick={() => toggleColor(itemIdx, color)}
                            data-testid={`process-restore-color-${item.id}-${color}`}>
                            + {color}
                          </button>
                        ))}
                      </div>
                    );
                  })()}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Sticky Action Footer */}
      {items.length > 0 && (
        <div className="sticky bottom-0 z-40 bg-white border-t shadow-lg -mx-6 px-6 py-4" data-testid="process-action-footer">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-4">
              <span className="text-sm text-gray-600">
                <strong>{totalDesigns}</strong> design{totalDesigns !== 1 ? 's' : ''} &middot; <strong>{totalPieces}</strong> pieces
              </span>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                className="border-emerald-400 text-emerald-700 hover:bg-emerald-50 gap-2"
                onClick={handleIssueBarcodes}
                disabled={loading || totalPieces === 0}
                data-testid="process-issue-barcode-btn"
              >
                <FileSpreadsheet className="w-4 h-4" />
                {loading ? 'Generating...' : 'Issue Barcode'}
              </Button>
              <Button
                className="bg-blue-600 hover:bg-blue-700 gap-2"
                onClick={handleCreateSE}
                disabled={totalPieces === 0}
                data-testid="process-create-se-btn"
              >
                <ArrowRightCircle className="w-4 h-4" />
                Create SE
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProcessSOItems;
